import { useContext } from "react";

import { Language } from "@safetyculture/s12-apis-connect-web/s12/common/languages_pb";
import { RoleAddress } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import { RoleInfo } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { OrgDetailsContext } from "../../context/organisation-context";
import { toUUID } from "../s12-helpers";

/**
 * Hook to access the complete orgDetails context data
 * @returns The raw orgDetails object containing all organization information
 */
export const useOrgDetails = () => {
  const { orgDetails } = useContext(OrgDetailsContext);

  return {
    orgDetails,
  };
};

/**
 * Hook to get the organization role object
 * @returns The Role object or null if not available
 */
export const useOrgRole = () => {
  const { orgDetails } = useContext(OrgDetailsContext);
  return orgDetails.role || null;
};

/**
 * Hook to get the S12 organization ID. i.e. role_f95c3ad1305d42bd889e7c68b76a2a23.
 * @returns The organization's s12 ID as a string, or empty string if not available
 */
export const useOrgS12ID = () => {
  const role = useOrgRole();
  return role?.id || "";
};

/**
 * Hook to get the organization UUID. This converts the S12 organization ID to UUID. i.e. role_f95c3ad1305d42bd889e7c68b76a2a23 to f95c3ad1-305d-42bd-889e-7c68b76a2a23
 * @returns The organization's UUID in standard format (with dashes), or empty string if not available
 */
export const useOrgUUID = () => {
  const role = useOrgRole();
  return role?.id ? toUUID(role.id) : "";
};

/**
 * Hook to get the organization name.
 * @returns The organization name as a string, or empty string if not available
 */
export const useOrgName = () => {
  const role = useOrgRole();
  return role?.name || "";
};

/**
 * Hook to get organization role information. role.info
 * @returns Object containing name, abn, email, phone, and language
 */
export const useOrgRoleInfo = () => {
  const role = useOrgRole();

  return (
    role?.info ||
    new RoleInfo({
      language: Language.EN_US, // default to EN_US
    })
  );
};

/**
 * Hook to get organization role information address. role.info.address
 * @returns Object containing role address.
 */
export const useOrgRoleInfoAddress = () => {
  const role = useOrgRole();

  return role?.info?.address || new RoleAddress({});
};

/**
 * Hook to get organization member counts
 * @returns Object containing member counts and total
 */
export const useOrgMemberCounts = () => {
  const role = useOrgRole();

  const activeMembers = role?.members?.length || 0;
  const inactiveMembers = role?.inactiveMembers?.length || 0;
  const totalMembers = activeMembers + inactiveMembers;

  return {
    activeMembers,
    inactiveMembers,
    totalMembers,
  };
};
